import React, { useState, useEffect } from "react";
import { Flex, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import {
  PollScreen,
  ChatScreen,
  QnAScreen,
  FormScreen,
  BroadcastScreen,
} from "../../components/spaces";
import axiosInstance from "../../routes/axiosInstance";

// --- Main SpacePage ---
const SpacePage = () => {
  const { referenceId, spaceNameSlug } = useParams();
  const [space, setSpace] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [member, setMember] = useState(null);
  const [activities, setActivities] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Expose fetchSpace so child components can trigger it
  const fetchSpace = async () => {
    setLoading(true);
    try {
      let guestAccessId = null;
      const memberData = localStorage.getItem(`member_${referenceId}`);
      if (memberData) {
        try {
          const parsed = JSON.parse(memberData);
          guestAccessId = parsed?.guestAccessId;
        } catch {}
      }
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: guestAccessId ? `Guest ${guestAccessId}` : undefined,
        },
      };
      const res = await axiosInstance.get(
        `/api/spaces/${referenceId}/public`,
        config,
      );
      setSpace(res.data.space);
      setAdmin(res.data.admin);
      setMember(res.data.member);
      setActivities(res.data.activities || {});
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load space",
      );
      if (err.response?.status === 401 || err.response?.status === 404) {
        // If unauthorized, redirect to login
        navigate(`/s/${referenceId}/${spaceNameSlug}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceId]);

  if (loading) {
    return (
      <Flex align="center" justify="center" minH="100vh">
        <Text>Loading...</Text>
      </Flex>
    );
  }
  if (error || !space) {
    return (
      <Flex align="center" justify="center" minH="100vh">
        <Text color="red.500">{error || "Space not found"}</Text>
      </Flex>
    );
  }

  switch (space.kind) {
    case "POLL":
      return (
        <PollScreen
          space={space}
          admin={admin}
          member={member}
          activities={activities}
          referenceId={referenceId}
          fetchSpace={fetchSpace}
        />
      );
    case "CHAT":
      return (
        <ChatScreen
          space={space}
          admin={admin}
          currentMember={member}
          activities={activities}
          referenceId={referenceId}
          spaceNameSlug={spaceNameSlug}
        />
      );
    case "QNA":
      return <QnAScreen space={space} admin={admin} member={member} />;
    case "FORM":
      return (
        <FormScreen
          space={space}
          admin={admin}
          member={member}
          referenceId={referenceId}
          fetchSpace={fetchSpace}
        />
      );
    case "BROADCAST":
      return (
        <BroadcastScreen
          space={space}
          admin={admin}
          member={member}
          activities={activities}
          referenceId={referenceId}
          fetchSpace={fetchSpace}
        />
      );
    default:
      return (
        <Flex align="center" justify="center" minH="100vh">
          <Text color="gray.500">Unsupported space type.</Text>
        </Flex>
      );
  }
};

export default SpacePage;
